/* eslint-disable */
import { useEffect, useState, useCallback } from "react";
import {
  Search,
  Plus,
  Clock,
  TrendingUp,
  Tag,
  CheckCircle2,
  FolderPlus,
  Pencil,
  Trash2,
} from "lucide-react";
import { docsApi, iconFor, shortDate } from "./api";
import DocumentCard from "./DocumentCard";
import BundleRow from "./BundleRow";
import CategoryDetail from "./CategoryDetail";
import UploadModal from "./UploadModal";
import ApprovalQueue from "./ApprovalQueue";
import CategoryModal from "./CategoryModal";
import { ViewToggle, Pager, useViewMode, DOCS_PAGE_SIZE } from "./DocsControls";
import useDebouncedValue from "../utils/useDebouncedValue";
import "./documents.css";

export default function MemberDocuments({ isAdmin, canApprove }) {
  const [view, setView] = useState("mostViewed"); // mostViewed | category | recent | tag | approvals
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedTag, setSelectedTag] = useState(null);
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [editCatObj, setEditCatObj] = useState(null); // null = create mode, object = edit mode
  const [bundles, setBundles] = useState([]);
  const [pendingBundle, setPendingBundle] = useState(null);
  const [viewMode, setViewMode] = useViewMode();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  const loadCategories = useCallback(async () => {
    try {
      const data = await docsApi.listCategories();
      setCategories(data.categories || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadPendingCount = useCallback(async () => {
    if (!canApprove) return;
    try {
      const data = await docsApi.pendingCount();
      setPendingCount(data.count || 0);
    } catch (e) {
      console.error(e);
    }
  }, [canApprove]);

  const loadDocs = useCallback(async () => {
    if (view !== "recent" && view !== "mostViewed" && view !== "tag") return;
    setLoading(true);
    try {
      const sort = view === "mostViewed" ? "mostViewed" : "newest";
      if (view === "tag") {
        // The tag cloud needs the whole corpus to be meaningful, so this
        // view fetches broadly and paginates/filters client-side.
        const data = await docsApi.list({
          sort,
          status_filter: "PUBLISHED",
          search: debouncedSearch || undefined,
          limit: 200,
        });
        setDocs(data.documents || []);
        setBundles([]);
        setTotal(data.total || (data.documents || []).length);
      } else {
        const [data, bundleData] = await Promise.all([
          docsApi.list({
            sort,
            status_filter: "PUBLISHED",
            search: debouncedSearch || undefined,
            limit: DOCS_PAGE_SIZE,
            offset: (page - 1) * DOCS_PAGE_SIZE,
          }),
          // Bundles surface as folder tiles in the flat lists too (not just
          // inside a category) so an uploaded bundle is visible up front.
          docsApi.listBundles(),
        ]);
        setDocs(data.documents || []);
        setBundles(bundleData.bundles || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [view, debouncedSearch, page]);

  // Reset to the first page whenever the listing or its filters change.
  useEffect(() => {
    setPage(1);
  }, [view, debouncedSearch, selectedTag]);

  useEffect(() => {
    loadCategories();
    loadPendingCount();
  }, [loadCategories, loadPendingCount, reloadKey]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs, reloadKey]);

  const refreshAll = () => setReloadKey((k) => k + 1);

  const getAllTags = () => {
    const m = new Map();
    docs.forEach((d) =>
      (d.tags || []).forEach((t) => m.set(t, (m.get(t) || 0) + 1))
    );
    return Array.from(m.entries())
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count);
  };

  const handleDeleteCategory = async (cat, e) => {
    if (e) e.stopPropagation();
    if (
      !window.confirm(
        `Delete the "${cat.label}" category? This can't be undone.`
      )
    )
      return;
    try {
      await docsApi.deleteCategory(cat.id);
      refreshAll();
    } catch (err) {
      alert(err.message || "Could not delete category");
    }
  };

  const openBundleTile = (b) => {
    setSelectedCategory(b.category);
    setPendingBundle({ id: b.id, name: b.name });
  };

  // One unified listing: folders (bundles) first, then files (documents).
  // Honours the list/folder view mode toggle.
  const renderDocs = (docList, withBundles) => {
    const tile = viewMode === "grid";
    return (
      <div className={tile ? "docs-tilegrid" : "docs-rowlist"}>
        {withBundles &&
          bundles.map((b) => (
            <BundleRow
              key={b.id}
              bundle={b}
              variant={tile ? "tile" : "row"}
              onOpen={openBundleTile}
            />
          ))}
        {docList.map((d) => (
          <DocumentCard
            key={d.id}
            doc={d}
            isAdmin={isAdmin}
            onChange={refreshAll}
            showCategory
            variant={tile ? "tile" : "row"}
          />
        ))}
      </div>
    );
  };

  if (selectedCategory) {
    return (
      <CategoryDetail
        category={selectedCategory}
        isAdmin={isAdmin}
        initialBundle={pendingBundle}
        onBack={() => {
          setSelectedCategory(null);
          setPendingBundle(null);
          refreshAll();
        }}
        onChange={refreshAll}
      />
    );
  }

  if (view === "approvals" && canApprove) {
    return (
      <ApprovalQueue
        onBack={() => {
          setView("mostViewed");
          refreshAll();
        }}
        onChange={refreshAll}
      />
    );
  }

  return (
    <div className="docs-wrap">
      <div className="docs-toolbar">
        <div className="docs-search-row">
          <div className="docs-search">
            <span className="docs-search-icon">
              <Search size={16} />
            </span>
            <input
              type="text"
              placeholder="Search documents by title, description, or tag…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {canApprove && (
            <button
              className={`docs-approvals-btn ${
                view === "approvals" ? "active" : ""
              }`}
              onClick={() => setView("approvals")}
              title="Review documents awaiting approval"
            >
              <CheckCircle2 size={16} /> Approvals
              {pendingCount > 0 && (
                <span className="docs-pending-badge">{pendingCount}</span>
              )}
            </button>
          )}
          <button className="docs-upload-btn" onClick={() => setUploadOpen(true)}>
            <Plus size={16} /> Upload Document
          </button>
        </div>

        <div className="docs-tabs">
          <button
            className={view === "mostViewed" ? "active" : ""}
            onClick={() => setView("mostViewed")}
          >
            <TrendingUp size={14} /> Most Viewed
          </button>
          <button
            className={view === "category" ? "active" : ""}
            onClick={() => setView("category")}
          >
            By Category
          </button>
          <button
            className={view === "recent" ? "active" : ""}
            onClick={() => setView("recent")}
          >
            <Clock size={14} /> Recent
          </button>
          <button
            className={view === "tag" ? "active" : ""}
            onClick={() => setView("tag")}
          >
            <Tag size={14} /> By Tag
          </button>
        </div>
      </div>

      <div className="docs-content">
        {view === "category" && (
          <div className="docs-cat-grid">
            {categories.map((c) => {
              const CatIcon = iconFor(c.icon);
              const color = c.color || "#475569";
              return (
                <button
                  key={c.key || c.name}
                  className="docs-cat-card"
                  onClick={() => setSelectedCategory(c.key || c.name)}
                >
                  <div className="docs-cat-top">
                    <span
                      className="docs-cat-icon"
                      style={{ background: `${color}1f`, color }}
                    >
                      <CatIcon size={20} strokeWidth={1.8} />
                    </span>
                    <span
                      className="docs-cat-count"
                      style={{ backgroundColor: color }}
                    >
                      {c.count}
                    </span>
                  </div>
                  <h3>{c.label}</h3>
                  <p>
                    {c.last_updated
                      ? `Last updated ${shortDate(c.last_updated)}`
                      : "No documents yet"}
                  </p>
                  {isAdmin && !c.is_system && (
                    <span className="docs-cat-actions" onClick={(e) => e.stopPropagation()}>
                      <span
                        className="docs-cat-edit"
                        role="button"
                        tabIndex={0}
                        title="Edit category"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditCatObj(c);
                          setCatModalOpen(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                            setEditCatObj(c);
                            setCatModalOpen(true);
                          }
                        }}
                      >
                        <Pencil size={13} />
                      </span>
                      <span
                        className="docs-cat-delete"
                        role="button"
                        tabIndex={0}
                        title="Delete category"
                        onClick={(e) => handleDeleteCategory(c, e)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ")
                            handleDeleteCategory(c, e);
                        }}
                      >
                        <Trash2 size={14} />
                      </span>
                    </span>
                  )}
                </button>
              );
            })}
            {isAdmin && (
              <button
                className="docs-cat-card docs-cat-card-new"
                onClick={() => { setEditCatObj(null); setCatModalOpen(true); }}
              >
                <div className="docs-cat-new-inner">
                  <FolderPlus size={22} strokeWidth={1.8} />
                  <h3>New Category</h3>
                  <p>Add a folder for the community</p>
                </div>
              </button>
            )}
          </div>
        )}

        {view === "recent" && (
          <>
            <div className="docs-section-head">
              <h2 className="docs-section-title">Recent</h2>
              <ViewToggle mode={viewMode} onChange={setViewMode} />
            </div>
            {loading && docs.length === 0 ? (
              <div className="docs-empty">Loading…</div>
            ) : docs.length === 0 && bundles.length === 0 ? (
              <div className="docs-empty">No documents yet</div>
            ) : (
              <>
                {renderDocs(docs, true)}
                <Pager
                  page={page}
                  pageSize={DOCS_PAGE_SIZE}
                  total={total}
                  onPage={setPage}
                />
              </>
            )}
          </>
        )}

        {view === "mostViewed" && (
          <>
            <div className="docs-section-head">
              <h2 className="docs-section-title">Most Viewed</h2>
              <ViewToggle mode={viewMode} onChange={setViewMode} />
            </div>
            {loading && docs.length === 0 ? (
              <div className="docs-empty">Loading…</div>
            ) : docs.length === 0 && bundles.length === 0 ? (
              <div className="docs-empty">No documents yet</div>
            ) : (
              <>
                {renderDocs(docs, true)}
                <Pager
                  page={page}
                  pageSize={DOCS_PAGE_SIZE}
                  total={total}
                  onPage={setPage}
                />
              </>
            )}
          </>
        )}

        {view === "tag" && (
          <>
            <h2 className="docs-section-title">Browse by Tag</h2>
            {loading && docs.length === 0 ? (
              <div className="docs-empty">Loading…</div>
            ) : getAllTags().length === 0 ? (
              <div className="docs-empty">No tags yet — upload a document with tags</div>
            ) : (
              <>
                <div className="docs-tag-cloud">
                  {getAllTags().map(({ tag, count }) => (
                    <button
                      key={tag}
                      className={`docs-tag ${selectedTag === tag ? "active" : ""}`}
                      onClick={() =>
                        setSelectedTag(selectedTag === tag ? null : tag)
                      }
                    >
                      {tag} <span>({count})</span>
                    </button>
                  ))}
                </div>
                {selectedTag ? (
                  <>
                    <div className="docs-tag-filter-bar">
                      <strong>Showing documents tagged:</strong>
                      <span className="docs-card-tag">{selectedTag}</span>
                      <button
                        className="docs-tag-filter-clear"
                        onClick={() => setSelectedTag(null)}
                      >
                        Clear filter
                      </button>
                      <span className="docs-tag-filter-spacer" />
                      <ViewToggle mode={viewMode} onChange={setViewMode} />
                    </div>
                    {(() => {
                      const filtered = docs.filter((d) =>
                        (d.tags || []).includes(selectedTag)
                      );
                      if (filtered.length === 0) {
                        return (
                          <div className="docs-empty">
                            No documents found for this tag
                          </div>
                        );
                      }
                      const start = (page - 1) * DOCS_PAGE_SIZE;
                      const pageItems = filtered.slice(
                        start,
                        start + DOCS_PAGE_SIZE
                      );
                      return (
                        <>
                          {renderDocs(pageItems, false)}
                          <Pager
                            page={page}
                            pageSize={DOCS_PAGE_SIZE}
                            total={filtered.length}
                            onPage={setPage}
                          />
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <p className="docs-card-note">
                    Click a tag to see all documents with that tag.
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        isAdmin={isAdmin}
        onUploaded={refreshAll}
      />

      <CategoryModal
        open={catModalOpen}
        editCat={editCatObj}
        onClose={() => { setCatModalOpen(false); setEditCatObj(null); }}
        onCreated={() => { setEditCatObj(null); refreshAll(); }}
      />
    </div>
  );
}
